import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';

export default function Dashboard() {
  const { user } = useAuth();
  const [captureMode, setCaptureMode] = useState<'text' | 'image' | 'audio' | 'video'>('text');
  const [textContent, setTextContent] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState<any>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleCapture = async () => {
    setIsProcessing(true);
    setResult(null);

    try {
      const formData = new FormData();
      
      if (captureMode === 'text') {
        formData.append('content', textContent);
        formData.append('type', 'text');
      } else if (selectedFile) {
        formData.append('file', selectedFile);
        formData.append('type', captureMode);
      }

      // Add context information
      formData.append('timestamp', new Date().toISOString());
      formData.append('location', 'Browser'); // TODO: Add geolocation

      // TODO: Send to backend for AI processing
      // const response = await axios.post('https://notiq-backend.madmagento.workers.dev/api/capture', formData);
      
      // Mock response for now
      setTimeout(() => {
        setResult({
          id: Date.now(),
          type: captureMode,
          content: captureMode === 'text' ? textContent : selectedFile?.name,
          timestamp: new Date().toISOString(),
          category: 'General',
          summary: 'Captured successfully'
        });
        setIsProcessing(false);
        setTextContent('');
        setSelectedFile(null);
      }, 2000);
    } catch (error) {
      console.error('Capture failed:', error);
      setIsProcessing(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Quick Capture</h2>

          {/* Capture Mode Selector */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <button
              onClick={() => setCaptureMode('text')}
              className={`p-4 rounded-lg border-2 transition ${
                captureMode === 'text'
                  ? 'border-blue-600 bg-blue-50 text-blue-600'
                  : 'border-gray-200 hover:border-blue-300'
              }`}
            >
              <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="text-sm font-medium">Text</span>
            </button>

            <button
              onClick={() => setCaptureMode('image')}
              className={`p-4 rounded-lg border-2 transition ${
                captureMode === 'image'
                  ? 'border-blue-600 bg-blue-50 text-blue-600'
                  : 'border-gray-200 hover:border-blue-300'
              }`}
            >
              <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className="text-sm font-medium">Image</span>
            </button>

            <button
              onClick={() => setCaptureMode('audio')}
              className={`p-4 rounded-lg border-2 transition ${
                captureMode === 'audio'
                  ? 'border-blue-600 bg-blue-50 text-blue-600'
                  : 'border-gray-200 hover:border-blue-300'
              }`}
            >
              <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
              </svg>
              <span className="text-sm font-medium">Audio</span>
            </button>

            <button
              onClick={() => setCaptureMode('video')}
              className={`p-4 rounded-lg border-2 transition ${
                captureMode === 'video'
                  ? 'border-blue-600 bg-blue-50 text-blue-600'
                  : 'border-gray-200 hover:border-blue-300'
              }`}
            >
              <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <span className="text-sm font-medium">Video</span>
            </button>
          </div>

          {/* Capture Input */}
          <div className="mb-6">
            {captureMode === 'text' ? (
              <textarea
                value={textContent}
                onChange={(e) => setTextContent(e.target.value)}
                placeholder="Type your notes here..."
                className="w-full h-40 p-4 border-2 border-gray-200 rounded-lg focus:border-blue-600 focus:outline-none"
              />
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <input
                  type="file"
                  accept={captureMode === 'image' ? 'image/*' : captureMode === 'audio' ? 'audio/*' : 'video/*'}
                  onChange={handleFileSelect}
                  className="hidden"
                  id="file-upload"
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p className="text-gray-600">{selectedFile ? selectedFile.name : `Click to upload ${captureMode}`}</p>
                </label>
              </div>
            )}
          </div>

          {/* Capture Button */}
          <button
            onClick={handleCapture}
            disabled={isProcessing || (captureMode === 'text' && !textContent) || (captureMode !== 'text' && !selectedFile)}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold py-3 px-6 rounded-lg transition"
          >
            {isProcessing ? 'Processing...' : 'Capture & Process'}
          </button>
        </div>

        {/* Result Display */}
        {result && (
          <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-green-800 mb-2">✓ Capture Successful</h3>
            <div className="text-sm text-gray-700">
              <p><span className="font-medium">Type:</span> {result.type}</p>
              <p><span className="font-medium">Category:</span> {result.category}</p>
              <p><span className="font-medium">Summary:</span> {result.summary}</p>
            </div>
          </div>
        )}

        {/* Recent Captures */}
        <div className="bg-white rounded-xl shadow-lg p-6 mt-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Recent Captures</h3>
          <p className="text-gray-500 text-center py-8">No recent captures yet. Start capturing!</p>
        </div>
      </div>
    </Layout>
  );
}
